import React, { useEffect, useState } from "react";
import { Stack } from "@mui/joy";
import {
  loadTossPayments,
  TossPaymentsPayment,
} from "@tosspayments/tosspayments-sdk";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { paymentOrderAtom } from "../../recoil/paymentOrderAtom";
import { useQuery } from "@tanstack/react-query";
import {
  PAYMENT_DOMAIN,
  PAYMENT_ORDER_DETAIL_ENDPOINT,
  paymentApi,
} from "../../api/paymentApi";
import useSafeAreaInsets from "../../hooks/useSafeAreaInsets";
import ContentTemplateV2 from "../../components/task/content/ContentTemplateV2";
import { useSearchParams } from "react-router-dom";
import {
  AFFILIATE_DISCOUNT,
  getMultiPlanPrices,
  getPromotionPrices,
  getSinglePlanPrice,
  PlanKey,
  planKeyToName,
} from "../../components/task/payment/planTypes";

export default function TossPayments() {
  const tossPaymentsClientKey = process.env.REACT_APP_TOSSPAYMENTS_CLIENT_KEY!!;
  const [searchParams] = useSearchParams();
  const planItemKey = (searchParams.get("plan") || "challenger") as PlanKey;

  const [tossPaymentsPayment, setTossPaymentsPayment] =
    useState<TossPaymentsPayment | null>();

  const user = useRecoilValue(userAtom);
  const [paymentOrderState, setPaymentOrderState] =
    useRecoilState(paymentOrderAtom);

  const { refetch: getPaymentOrder } = useQuery(
    [PAYMENT_DOMAIN, PAYMENT_ORDER_DETAIL_ENDPOINT],
    () => paymentApi.getOrderDetail(),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data && data.result) {
          setPaymentOrderState(data.result);
        }
      },
    }
  );

  useEffect(() => {
    if (user?.accessToken) {
      getPaymentOrder();
    } else {
      console.error("유저 정보 없음");
    }
  }, []);

  function computeAmountValue(): number {
    const base = computeBaseAmount();
    // 제휴 할인 활성 시 챌린저/마스터 -30,000 (표시가·서버 resolvePlan 과 동일 규칙)
    if (
      paymentOrderState.affiliateDiscountActive &&
      (planItemKey === "challenger" || planItemKey === "master") &&
      !paymentOrderState.isCarePass &&
      !getPromotionPrices(user?.promotion)
    ) {
      return base - AFFILIATE_DISCOUNT;
    }
    return base;
  }

  function computeBaseAmount(): number {
    if (paymentOrderState.isCarePass) return 36500;

    // 제휴 프로모션(타이탄/코리안매니아 등) 전용가: pricingModel 오버라이드보다 우선
    const promotionPrices = getPromotionPrices(user?.promotion);
    if (promotionPrices) {
      return promotionPrices[planItemKey] ?? promotionPrices.challenger;
    }

    const pricingModel = user?.pricingModel;

    // e/h/i: 단일 "맞춤 플랜"이 challenger로 매핑되며 모델별 가격 오버라이드
    const singlePlanPrice = getSinglePlanPrice(pricingModel);
    if (singlePlanPrice !== undefined && planItemKey === "challenger") {
      return singlePlanPrice;
    }

    // aeg: 다중 플랜 전체 가격 오버라이드
    const multiPlanPrices = getMultiPlanPrices(pricingModel);
    if (multiPlanPrices) {
      return multiPlanPrices[planItemKey] ?? multiPlanPrices.challenger;
    }

    // 기본 가격
    if (planItemKey === "starter") return 295000;
    if (planItemKey === "challenger") return 395000;
    if (planItemKey === "master") return 465000;
    return 395000;
  }

  async function requestTossPayments() {
    if (tossPaymentsPayment) {
      try {
        await tossPaymentsPayment?.requestPayment({
          method: "CARD",
          amount: {
            currency: "KRW",
            value: computeAmountValue(),
          },
          orderId: paymentOrderState.orderId!!,
          orderName: `디스턴싱 ${planKeyToName[planItemKey]} 플랜 / 100일${paymentOrderState?.isCarePass ? " (CarePass 적용가)" : ""}`,
          successUrl: window.location.origin + "/toss-payments/success",
          failUrl: window.location.origin + "/toss-payments/fail",
          customerEmail: paymentOrderState.customerEmail,
          customerName: paymentOrderState.customerName,
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
      } catch (error) {
        // 에러 처리하기
        console.error(error);
      }
    }
  }

  useEffect(() => {
    async function fetchTossPaymentsPayment() {
      if (tossPaymentsClientKey && paymentOrderState?.customerKey) {
        const tossPayments = await loadTossPayments(tossPaymentsClientKey);
        const payment = tossPayments.payment({
          customerKey: paymentOrderState.customerKey,
        });

        setTossPaymentsPayment(payment);
      }
    }

    fetchTossPaymentsPayment();
  }, [tossPaymentsClientKey, paymentOrderState]);

  useEffect(() => {
    if (tossPaymentsPayment) {
      requestTossPayments();
    }
  }, [tossPaymentsPayment]);

  const { bottom: safeBottom } = useSafeAreaInsets();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - safeBottom
  );

  return (
    <ContentTemplateV2
      fullBleed
      disableBottomSafeArea
      isWide
      setContainerHeight={setContainerHeight}
    >
      {containerHeight > 0 && (
        <Stack sx={{ pb: "150px", opacity: 0 }}>
          <Stack
            spacing={"30px"}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              width: "100%",
              maxWidth: "660px",
              mx: "auto",
              px: "20px",
            }}
          ></Stack>
        </Stack>
      )}
    </ContentTemplateV2>
  );
}
